import type { PillColor } from '@wasabi/actions'
import { formatCurrency } from '@/utils/dateUtils'
import { newestFirst, timelineInstant } from '@/utils/newest-first'
import type { TimelineEvent, TimelineKind } from './ticket-timeline.types'

const KIND_LABELS: Record<TimelineKind, string> = {
  run: 'Fleet run',
  pr: 'Pull request',
  comment: 'Comment',
  decision: 'Decision'
}

export function timelineKindLabel(kind: TimelineKind): string {
  return KIND_LABELS[kind] ?? kind
}

const STATE_COLORS: Record<string, PillColor> = {
  ok: 'green',
  running: 'blue',
  skipped: 'gray',
  budget: 'yellow',
  error: 'red',
  OPEN: 'blue',
  MERGED: 'green',
  DECLINED: 'gray',
  SUPERSEDED: 'gray',
  open: 'yellow',
  answered: 'green',
  relayed: 'gray'
}

export function timelineStateColor(state: string | undefined): PillColor {
  return STATE_COLORS[state ?? ''] ?? 'gray'
}

/** A run's cost, when the record has one. */
export function timelineCost(event: Pick<TimelineEvent, 'costUsd'>): string {
  return typeof event.costUsd === 'number' ? formatCurrency(event.costUsd) : ''
}

/** "2026-10-14" in the viewer's own time zone, so a Jira time (`-0400`) and a fleet time (UTC) land on the same day. */
export function viewerDay(at: string | undefined): string {
  const date = new Date(timelineInstant(at))
  if (Number.isNaN(date.getTime())) return 'Undated'
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** Events by the viewer's day, newest day first, each day's events newest first. */
export function timelineDays(events: TimelineEvent[]): { day: string; events: TimelineEvent[] }[] {
  const days = new Map<string, TimelineEvent[]>()
  newestFirst(events, event => event.at).forEach(event => {
    const day = viewerDay(event.at)
    days.set(day, [...(days.get(day) ?? []), event])
  })
  return [...days.entries()].map(([day, dayEvents]) => ({ day, events: dayEvents }))
}
